import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, ShieldCheck, FileText, Headphones } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OFFERS, useCheckout } from "@/lib/checkout";
import { track } from "@/lib/track";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const CheckoutButton = ({ lookup, label, testid, className, name: nameOverride, price: priceOverride }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [touched, setTouched] = useState(false);
  const { go, loading } = useCheckout();
  const offer = {
    name: nameOverride || (OFFERS[lookup] ? OFFERS[lookup].name : lookup),
    price: priceOverride || (OFFERS[lookup] ? OFFERS[lookup].price : ""),
  };
  const emailValid = EMAIL_RE.test(email);

  const openModal = () => {
    track("click_cta", { offer: lookup });
    setOpen(true);
  };

  const submit = async () => {
    setTouched(true);
    if (!emailValid || !ack) return;
    await go(lookup, email);
  };

  return (
    <>
      <button data-testid={testid} onClick={openModal} className={className}>
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={`${testid}-modal`}
          className="bg-obsidian-800 border-gold/25 text-slate-200 max-w-md rounded-3xl p-0 gap-0 overflow-hidden"
        >
          <div className="p-7">
            <DialogHeader className="text-left space-y-2">
              <DialogTitle className="font-display text-xl text-white">
                {offer.name} — <span className="text-gold">{offer.price}</span>
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                One-time payment. Enter your email — your receipt, intake link and portal access go
                here.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6">
              <label
                htmlFor={`${testid}-email-field`}
                className="block text-[10px] font-mono uppercase tracking-[0.25em] text-slate-300 mb-2"
              >
                Email
              </label>
              <input
                id={`${testid}-email-field`}
                data-testid={`${testid}-email-input`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                aria-invalid={touched && !emailValid}
                className="w-full rounded-xl border border-slate-600 bg-obsidian-900/80 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40"
              />
              {touched && !emailValid && (
                <p data-testid={`${testid}-email-error`} className="text-red-300 text-xs mt-2">
                  Please enter a valid email address.
                </p>
              )}
            </div>

            <div className="mt-5 rounded-2xl border border-gold/25 bg-obsidian-900/60 p-4">
              <div className="flex gap-3">
                <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed">
                  <span className="text-gold font-semibold">7-Day Money-Back Guarantee.</span> This
                  offer is covered if it is guarantee-eligible at checkout: refund requests within 7
                  calendar days of purchase. Conditions and exclusions apply — for example, work
                  already delivered and nonrecoverable third-party costs. See the{" "}
                  <Link
                    to="/refund-policy"
                    data-testid={`${testid}-refund-link`}
                    className="text-gold underline underline-offset-2"
                    onClick={() => setOpen(false)}
                  >
                    Refund Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-300">
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-emerald-400" /> Secure Checkout</span>
                <span className="inline-flex items-center gap-1.5"><FileText className="w-3 h-3 text-emerald-400" /> Clear Scope</span>
                <span className="inline-flex items-center gap-1.5"><Headphones className="w-3 h-3 text-emerald-400" /> Human Support</span>
              </div>
            </div>

            <label className="flex gap-3 items-start mt-5 cursor-pointer select-none" htmlFor={`${testid}-ack`}>
              <input
                id={`${testid}-ack`}
                data-testid={`${testid}-ack-checkbox`}
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#D4AF37]"
              />
              <span className="text-xs text-slate-300 leading-relaxed">
                I have read the guarantee conditions and understand that eligible purchases are
                refundable within 7 calendar days, subject to the Refund Policy.
              </span>
            </label>

            <button
              data-testid={`${testid}-continue`}
              onClick={submit}
              disabled={loading === lookup}
              className="mt-6 w-full rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
            >
              {loading === lookup && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading === lookup ? "Opening secure checkout…" : "Continue to Secure Checkout"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CheckoutButton;
